import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Check, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/permissions";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function EditableSelect({ optionsKey, value, onChange, placeholder, testId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = isAdmin(user?.role);
  const [adding, setAdding] = useState(false);
  const [newVal, setNewVal] = useState("");

  const { data } = useQuery({
    queryKey: ["options", optionsKey],
    queryFn: async () => (await api.get(`/options/${optionsKey}`)).data.values || [],
  });

  const values = data || [];

  const addOption = async () => {
    const v = newVal.trim();
    if (!v) return;
    const res = await api.post(`/options/${optionsKey}`, { value: v });
    qc.setQueryData(["options", optionsKey], res.data.values);
    onChange(v);
    setNewVal("");
    setAdding(false);
  };

  const deleteOption = async (v) => {
    if (!window.confirm(`Remove "${v}" from the list? Existing records keep this value.`)) return;
    const res = await api.delete(`/options/${optionsKey}`, { data: { value: v } });
    qc.setQueryData(["options", optionsKey], res.data.values);
    if (value === v) onChange("");
    toast.success(`Removed "${v}"`);
  };

  return (
    <div className="space-y-2">
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}>
          <SelectValue placeholder={placeholder || "Select..."} />
        </SelectTrigger>
        <SelectContent>
          {values.map((v) => (
            <div key={v} className="flex items-center gap-1 pr-1">
              <SelectItem value={v} className="flex-1" data-testid={`${testId}-opt-${v}`}>
                {v}
              </SelectItem>
              {canDelete && (
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteOption(v);
                  }}
                  className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                  title={`Delete ${v}`}
                  data-testid={`${testId}-del-opt-${v}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </SelectContent>
      </Select>

      {adding ? (
        <div className="flex gap-2">
          <Input
            autoFocus
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOption())}
            placeholder="New option..."
            className="h-8"
            data-testid={`${testId}-new-input`}
          />
          <Button type="button" size="sm" className="h-8" onClick={addOption} data-testid={`${testId}-save-opt`}>
            <Check className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
          data-testid={`${testId}-add-opt-btn`}
        >
          <Plus className="h-3 w-3" /> Add option
        </button>
      )}
    </div>
  );
}
